import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import { printPoolReceipt } from '../utils/print';
import * as api from '../api/client';
import ComplimentaryModal from '../components/ComplimentaryModal';

// ── Inline SVG icon component ─────────────────────────────────────────────────
const Ico = ({ d, size=16, stroke='currentColor', sw=2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke}
    strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display:'inline-block', verticalAlign:'middle', flexShrink:0 }}>
    {(Array.isArray(d) ? d : [d]).map((p,i) => <path key={i} d={p}/>)}
  </svg>
);

// ── Swimming Rates ────────────────────────────────────────────────────────────
const SWIM_RATES = [
  { type:'adult_resident',    label:'Adult Resident',       price:500  },
  { type:'child_resident',    label:'Child Resident (<12)', price:300  },
  { type:'adult_nonresident', label:'Adult Non-Resident',   price:1000 },
  { type:'child_nonresident', label:'Child Non-Resident',   price:600  },
  { type:'hotel_guest',       label:'Hotel Guest (FOC)',    price:0    },
];

// ── Extras & Add-ons ──────────────────────────────────────────────────────────
const EXTRAS = [
  { id:'costume_adult', cat:'gear',         label:'Swimming Costume – Adult', price:500, desc:'Hire per session',         icon:['M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.57a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.57a2 2 0 00-1.34-2.23z'] },
  { id:'costume_child', cat:'gear',         label:'Swimming Costume – Child', price:300, desc:'Hire per session',         icon:['M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.57a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.57a2 2 0 00-1.34-2.23z'] },
  { id:'goggles',       cat:'gear',         label:'Goggles',                  price:200, desc:'Per session',              icon:['M2 12h4','M18 12h4','M6 9a4 4 0 004 4M14 13a4 4 0 004-4','M10 13a2 2 0 104 0 2 2 0 00-4 0'] },
  { id:'towel',         cat:'gear',         label:'Towel Hire',               price:150, desc:'Per towel',               icon:['M12 2v20','M2 7h20','M2 17h20'] },
  { id:'float_ring',    cat:'gear',         label:'Float / Swim Ring',        price:100, desc:'Per session',              icon:['M12 2a10 10 0 100 20A10 10 0 0012 2z','M12 8a4 4 0 100 8 4 4 0 000-8z'] },
  { id:'water_slide',   cat:'kids',         label:'Water Slide',              price:200, desc:'Per session, kids only',   icon:['M3 17l3-8 4 4 4-6 4 10','M3 20h18'] },
  { id:'trampoline',    cat:'kids',         label:'Trampoline',               price:300, desc:'Per 30 mins',              icon:['M4 6h16','M4 18h16','M6 6l-2 12','M18 6l2 12'] },
  { id:'mini_golf',     cat:'kids',         label:'Mini Golf',                price:400, desc:'Per round',               icon:['M12 18h.01','M5 22l7-20 7 20','M5 14h14'] },
  { id:'bouncy_castle', cat:'kids',         label:'Bouncy Castle',            price:200, desc:'Per 30 mins',              icon:['M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z','M9 22V12h6v10'] },
  { id:'face_paint',    cat:'kids',         label:'Face Painting',            price:250, desc:'Per child',               icon:['M12 2a10 10 0 100 20A10 10 0 0012 2z','M8 14s1.5 2 4 2 4-2 4-2','M9 9h.01','M15 9h.01'] },
  { id:'arts_crafts',   cat:'kids',         label:'Arts & Crafts',            price:350, desc:'Materials included',       icon:['M12 19l7-7 3 3-7 7-3-3z','M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z','M2 2l7.586 7.586','M11 11h.01'] },
  { id:'sunbed',        cat:'lounging',     label:'Sun Bed / Lounger',        price:300, desc:'Per day',                 icon:['M2 12h20','M12 2v4','M12 18v4','M4.93 4.93l2.83 2.83','M16.24 16.24l2.83 2.83','M2 12h4','M18 12h4'] },
  { id:'umbrella',      cat:'lounging',     label:'Pool Umbrella',            price:200, desc:'Per day',                 icon:['M23 12a11.05 11.05 0 00-22 0zm-5 7a3 3 0 01-6 0v-7'] },
  { id:'towel_premium', cat:'lounging',     label:'Premium Towel Set',        price:400, desc:'2 large towels + robe',   icon:['M12 2v20','M2 7h20','M2 17h20','M5 7v10','M19 7v10'] },
];

const EXTRA_CATS = [
  { id:'all',     label:'All Extras'      },
  { id:'gear',    label:'🩱 Swim Gear'    },
  { id:'kids',    label:'🧒 Kids Zone'    },
  { id:'lounging',label:'☀️ Sun Lounging' },
];

const PAY_MODES = [
  { value:'cash',          label:'Cash',          color:'#2ecc71' },
  { value:'mpesa',         label:'M-Pesa',        color:'#00b300' },
  { value:'card',          label:'Card / PDQ',    color:'#9b59b6' },
  { value:'room_charge',   label:'Room Charge',   color:'#e67e22' },
  { value:'stripe',        label:'Stripe',        color:'#635bff' },
  { value:'complimentary', label:'Complimentary', color:'#c8a96e' },
];

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SwimmingPage({ notify }) {
  const [tab, setTab]           = useState('entry');
  const [entries, setEntries]   = useState([]);
  const [guests, setGuests]     = useState([]);
  const [extrasCat, setExtrasCat] = useState('all');
  const [histFilter, setHistFilter] = useState('today');
  const [showComp, setShowComp] = useState(false);

  const blank = { visitor_name:'', visitor_type:'adult_resident', adults:1, children:0, payment_mode:'cash', tx_code:'', guest_id:'' };
  const [form, setForm]         = useState(blank);
  const [cart, setCart]         = useState([]);
  const [busy, setBusy]         = useState(false);
  const [mpesaLoading, setMpesaLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, g] = await Promise.all([api.getPoolEntries(), api.getGuests()]);
      setEntries(e.data.results || e.data);
      setGuests((g.data.results || g.data).filter(x => x.status === 'checked-in'));
    } catch {}
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  // ── Calculations ────────────────────────────────────────────────────────────
  const selRate  = SWIM_RATES.find(r => r.type === form.visitor_type) || SWIM_RATES[0];
  const adults   = parseInt(form.adults) || 1;
  const children = parseInt(form.children) || 0;
  const childRate = { adult_resident:300, adult_nonresident:600 }[form.visitor_type] || 0;
  const swimAmt  = selRate.price === 0 ? 0 : selRate.price * adults + childRate * children;
  const extrasAmt= cart.reduce((s,i) => s + i.price*(i.qty||1), 0);
  const totalAmt = swimAmt + extrasAmt;

  // ── Cart helpers ─────────────────────────────────────────────────────────────
  const addToCart = (ex) => setCart(c => {
    const f = c.find(x => x.id === ex.id);
    return f ? c.map(x => x.id===ex.id ? {...x,qty:(x.qty||1)+1} : x) : [...c, {...ex,qty:1}];
  });
  const decCart = (id) => setCart(c => {
    const f = c.find(x=>x.id===id);
    if (f && (f.qty||1)>1) return c.map(x=>x.id===id?{...x,qty:x.qty-1}:x);
    return c.filter(x=>x.id!==id);
  });

  // ── Summary stats ─────────────────────────────────────────────────────────
  const today  = new Date().toISOString().slice(0,10);
  const month  = today.slice(0,7);
  const todayE = entries.filter(e=>(e.created_at||'').startsWith(today));
  const monthE = entries.filter(e=>(e.created_at||'').startsWith(month));
  const sum    = (arr) => arr.reduce((s,e)=>s+parseFloat(e.total||e.amount||0),0);
  const inPool = todayE.filter(e=>!e.exit_time).length;

  // ── Payment helpers ──────────────────────────────────────────────────────────
  const triggerMpesa = async () => {
    if (!form.visitor_name.trim()) { notify('Enter visitor name','error'); return; }
    const phone = prompt('M-Pesa number (254XXXXXXXXX):');
    if (!phone) return;
    setMpesaLoading(true);
    try {
      const r = await api.mpesaStkPush({ phone, amount:totalAmt||100, description:'Pool & Kids Zone' });
      notify('M-Pesa STK push sent!');
      setForm(f=>({...f,payment_mode:'mpesa',tx_code:r.data.CheckoutRequestID||''}));
    } catch(e){ notify(e.response?.data?.error||'M-Pesa failed','error'); }
    finally { setMpesaLoading(false); }
  };

  const triggerStripe = async () => {
    if (!form.visitor_name.trim()) { notify('Enter visitor name','error'); return; }
    setStripeLoading(true);
    try {
      const r = await api.stripePayIntent({ amount:(totalAmt||100)*100, currency:'kes', description:'Pool Entry' });
      notify('Stripe intent created');
      setForm(f=>({...f,payment_mode:'stripe',tx_code:r.data.id||''}));
    } catch(e){ notify(e.response?.data?.error||'Stripe failed','error'); }
    finally { setStripeLoading(false); }
  };

  const submitEntry = async () => {
    if (!form.visitor_name.trim()) { notify('Enter visitor name','error'); return; }
    if (form.payment_mode === 'complimentary') {
      setShowComp(true); return;
    }
    setBusy(true);
    try {
      const r = await api.createPoolEntry({
        visitor_name: form.visitor_name,
        visitor_type: form.visitor_type,
        adults, children,
        payment_mode: form.payment_mode,
        transaction_code: form.tx_code,
        guest: form.guest_id||null,
        amount: totalAmt,
        extras: cart.map(x=>({id:x.id,label:x.label,qty:x.qty||1,unit_price:x.price})),
        extras_total: extrasAmt,
      });
      const saved = r?.data || { visitor_name: form.visitor_name, visitor_type: form.visitor_type, adults, children, payment_mode: form.payment_mode, tx_code: form.tx_code, amount: totalAmt };
      await load();
      notify(`✓ Entry recorded — ${form.visitor_name} · ${fmtKES(totalAmt)}`);
      printPoolReceipt(saved);
      setForm(blank); setCart([]);
    } catch(e){ notify(e.response?.data?.error||'Error recording entry','error'); }
    finally { setBusy(false); }
  };

  // ── History filter ─────────────────────────────────────────────────────────
  const histEntries = histFilter==='today' ? todayE : histFilter==='month' ? monthE : entries.slice(0,300);
  const histTotal   = sum(histEntries);

  const pmColor = (mode) => PAY_MODES.find(p=>p.value===mode)?.color || '#888';

  return (
    <div>
      <PageHeader icon="🏊" title="Swimming Pool & Kids Zone" sub="Entry · Swim gear · Kids activities · Payment history" />

      <StatCards stats={[
        { label:"Today's Entries",  value: todayE.length,         color:'#3498db', icon:'🏊' },
        { label:"Today's Revenue",  value: fmtKES(sum(todayE)),   color:'#c8a96e', icon:'💰' },
        { label:'Currently In Pool',value: inPool,                color:'#2ecc71', icon:'👥' },
        { label:'Month Revenue',    value: fmtKES(sum(monthE)),   color:'#9b59b6', icon:'📅' },
        { label:'Pool Hours',       value: '6AM – 10PM',          color:'#e67e22', icon:'🕐' },
      ]} />

      <TabBar tabs={[
        { id:'entry',   label:'Pool Entry',    icon:'🏊' },
        { id:'extras',  label:'Swim Extras',   icon:'🩱' },
        { id:'history', label:'Payments',      icon:'📋' },
        { id:'rates',   label:'Rates & Info',  icon:'💰' },
      ]} active={tab} onChange={setTab} />

      {/* ═══════════════════════════ ENTRY ═══════════════════════════════════ */}
      {tab === 'entry' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:16 }}>
          <Card title="Record Pool Entry">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12 }}>
              <Field label="Visitor / Guardian Name *">
                <input style={inputStyle} value={form.visitor_name}
                  onChange={e=>setForm({...form,visitor_name:e.target.value})} placeholder="Full name" />
              </Field>
              <Field label="Visitor Type">
                <select style={inputStyle} value={form.visitor_type}
                  onChange={e=>setForm({...form,visitor_type:e.target.value})}>
                  {SWIM_RATES.map(r=>(
                    <option key={r.type} value={r.type}>
                      {r.label} {r.price===0?'— Free':('— '+fmtKES(r.price)+'/person')}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Number of Adults">
                <input style={inputStyle} type="number" min="0" value={form.adults}
                  onChange={e=>setForm({...form,adults:e.target.value})} />
              </Field>
              <Field label="Children (<12 yrs)">
                <input style={inputStyle} type="number" min="0" value={form.children}
                  onChange={e=>setForm({...form,children:e.target.value})} />
              </Field>
              {guests.length > 0 && (
                <Field label="Link Hotel Guest (auto-FOC)">
                  <select style={inputStyle} value={form.guest_id}
                    onChange={e=>setForm({...form,guest_id:e.target.value,
                      visitor_type:e.target.value?'hotel_guest':form.visitor_type})}>
                    <option value="">— Walk-in / External —</option>
                    {guests.map(g=><option key={g.id} value={g.id}>{g.name} · Rm {g.room_number}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Payment Method">
                <select style={inputStyle} value={form.payment_mode}
                  onChange={e=>setForm({...form,payment_mode:e.target.value})}>
                  {PAY_MODES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
              {['mpesa','card','stripe'].includes(form.payment_mode) && (
                <Field label="Transaction Reference">
                  <input style={inputStyle} value={form.tx_code}
                    onChange={e=>setForm({...form,tx_code:e.target.value})} placeholder="Code / Ref…" />
                </Field>
              )}
            </div>

            {/* Quick-add gear */}
            <div style={{ marginTop:14 }}>
              <div style={{ fontSize:10, color:'var(--textMuted)', letterSpacing:1, textTransform:'uppercase', fontWeight:700, marginBottom:8 }}>
                Quick-Add Swim Gear
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {EXTRAS.filter(e=>e.cat==='gear').map(ex=>{
                  const inCart = cart.find(c=>c.id===ex.id);
                  return (
                    <button key={ex.id} onClick={()=>addToCart(ex)} style={{
                      padding:'5px 11px', borderRadius:6, cursor:'pointer', fontFamily:'inherit',
                      border:`1px solid ${inCart?'var(--primary)':'var(--border)'}`,
                      background:inCart?'var(--primary)22':'var(--bgInput)',
                      color:inCart?'var(--primary)':'var(--textSecond)',
                      fontSize:11, fontWeight:600, display:'flex', alignItems:'center', gap:5,
                    }}>
                      {ex.label.split('–')[1]?.trim()||ex.label.split(' ').slice(-1)[0]}
                      <span style={{ color:'#c8a96e' }}>+{fmtKES(ex.price)}</span>
                      {inCart && <span style={{ background:'var(--primary)', color:'#111', borderRadius:999, padding:'1px 6px', fontSize:9 }}>×{inCart.qty||1}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button onClick={triggerMpesa} disabled={mpesaLoading}
                style={{ flex:1, background:'#00b300', border:'none', borderRadius:6, padding:'9px',
                  color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>
                {mpesaLoading ? 'Sending…' : '📱 M-Pesa STK'}
              </button>
              <button onClick={triggerStripe} disabled={stripeLoading}
                style={{ flex:1, background:'#635bff', border:'none', borderRadius:6, padding:'9px',
                  color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>
                {stripeLoading ? '…' : '💳 Stripe'}
              </button>
            </div>
          </Card>

          {/* Bill panel */}
          <Card title="Bill Summary">
            <Field label="Payment Method">
              <select style={inputStyle} value={form.payment_mode}
                onChange={e=>setForm({...form,payment_mode:e.target.value})}>
                {PAY_MODES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>

            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Swim Entry</div>
              <BillLine label={`${selRate.label} × ${adults}`} amount={selRate.price===0?0:selRate.price*adults} foc={selRate.price===0} />
              {children>0 && <BillLine label={`Children × ${children}`} amount={childRate*children} />}
            </div>

            {cart.length > 0 && (
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Extras</div>
                {cart.map(item=>(
                  <div key={item.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', borderBottom:'1px solid var(--border)' }}>
                    <span style={{ fontSize:12, color:'var(--textSecond)' }}>{item.label} {(item.qty||1)>1?`×${item.qty}`:''}</span>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <span style={{ fontWeight:700, color:'#c8a96e', fontSize:12 }}>{fmtKES(item.price*(item.qty||1))}</span>
                      <button onClick={()=>decCart(item.id)} style={{ background:'#e74c3c22', border:'none', borderRadius:3, width:20, height:20, cursor:'pointer', color:'#e74c3c', fontWeight:900 }}>−</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0 4px', borderTop:`2px solid var(--primary)` }}>
              <span style={{ fontWeight:800, fontSize:14, color:'var(--textPrimary)' }}>TOTAL</span>
              <span style={{ fontWeight:900, fontSize:18, color:'#c8a96e' }}>
                {selRate.price===0 && extrasAmt===0 ? 'FOC' : fmtKES(totalAmt)}
              </span>
            </div>

            <button style={{ ...btnPrimary, marginTop:10 }} onClick={submitEntry} disabled={busy}>
              {busy ? '⏳ Recording…' : '✓ Confirm Entry'}
            </button>
            {cart.length>0 && (
              <button onClick={()=>setCart([])} style={{ ...btnSm, width:'100%', marginTop:6, textAlign:'center' }}>
                Clear Extras
              </button>
            )}
          </Card>
        </div>
      )}

      {/* ═══════════════════════════ EXTRAS ═══════════════════════════════════ */}
      {tab === 'extras' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:16 }}>
          <div>
            <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
              {EXTRA_CATS.map(c=>(
                <button key={c.id} onClick={()=>setExtrasCat(c.id)} style={{
                  padding:'6px 14px', borderRadius:20, fontSize:11, fontWeight:700, cursor:'pointer',
                  border:`1px solid ${extrasCat===c.id?'var(--primary)':'var(--border)'}`,
                  background:extrasCat===c.id?'var(--primary)22':'var(--bgInput)',
                  color:extrasCat===c.id?'var(--primary)':'var(--textMuted)',
                }}>{c.label}</button>
              ))}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:12 }}>
              {EXTRAS.filter(e=>extrasCat==='all'||e.cat===extrasCat).map(ex=>{
                const inCart = cart.find(c=>c.id===ex.id);
                return (
                  <div key={ex.id} onClick={()=>addToCart(ex)} style={{
                    background:'var(--bgCard)', border:`2px solid ${inCart?'var(--primary)':'var(--border)'}`,
                    borderRadius:10, padding:14, cursor:'pointer', transition:'all .15s',
                    boxShadow: inCart?'0 4px 16px rgba(200,169,110,0.2)':'0 1px 6px rgba(0,0,0,0.05)',
                  }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                      <div style={{ width:40, height:40, borderRadius:8,
                        background:inCart?'var(--primary)22':'var(--bgInput)',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Ico d={ex.icon} size={20} stroke={inCart?'var(--primary)':'var(--textMuted)'} />
                      </div>
                      {inCart && (
                        <span style={{ background:'var(--primary)', color:'#111', borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:800 }}>×{inCart.qty||1}</span>
                      )}
                    </div>
                    <div style={{ fontWeight:700, color:'var(--textPrimary)', fontSize:12, marginBottom:2 }}>{ex.label}</div>
                    <div style={{ fontSize:10, color:'var(--textMuted)', marginBottom:8 }}>{ex.desc}</div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontWeight:800, color:'#c8a96e', fontSize:14 }}>{fmtKES(ex.price)}</span>
                      <span style={{ fontSize:10, color:inCart?'var(--primary)':'var(--textMuted)', fontWeight:700 }}>
                        {inCart ? '✓ Added' : '+ Add'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cart */}
          <Card title="Cart">
            <Field label="Guest / Visitor Name">
              <input style={inputStyle} value={form.visitor_name}
                onChange={e=>setForm({...form,visitor_name:e.target.value})} placeholder="Name…" />
            </Field>
            <Field label="Payment">
              <select style={inputStyle} value={form.payment_mode}
                onChange={e=>setForm({...form,payment_mode:e.target.value})}>
                {PAY_MODES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            {cart.length === 0
              ? <Empty text="Click items to add them" />
              : (<>
                  {cart.map(item=>(
                    <div key={item.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                      padding:'7px 0', borderBottom:'1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color:'var(--textPrimary)' }}>{item.label}</div>
                        <div style={{ fontSize:10, color:'var(--textMuted)' }}>{fmtKES(item.price)} each</div>
                      </div>
                      <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                        <span style={{ fontWeight:800, color:'#c8a96e', fontSize:12, minWidth:60, textAlign:'right' }}>
                          {fmtKES(item.price*(item.qty||1))}
                        </span>
                        <button onClick={()=>decCart(item.id)} style={{ background:'#e74c3c22', border:'none', borderRadius:4, width:22, height:22, cursor:'pointer', color:'#e74c3c', fontWeight:900 }}>−</button>
                        <span style={{ fontSize:12, fontWeight:700, minWidth:16, textAlign:'center' }}>{item.qty||1}</span>
                        <button onClick={()=>addToCart(item)} style={{ background:'#2ecc7122', border:'none', borderRadius:4, width:22, height:22, cursor:'pointer', color:'#2ecc71', fontWeight:900 }}>+</button>
                      </div>
                    </div>
                  ))}
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0 6px', borderTop:'2px solid var(--primary)', fontWeight:800, fontSize:15 }}>
                    <span style={{ color:'var(--textPrimary)' }}>Total</span>
                    <span style={{ color:'#c8a96e' }}>{fmtKES(extrasAmt)}</span>
                  </div>
                  <button style={btnPrimary} onClick={submitEntry} disabled={busy||!form.visitor_name.trim()}>
                    {busy?'Recording…':'✓ Charge Extras'}
                  </button>
                  <button onClick={()=>setCart([])} style={{ ...btnSm, width:'100%', marginTop:6, textAlign:'center' }}>Clear</button>
                </>)
            }
          </Card>
        </div>
      )}

      {/* ═══════════════════════════ PAYMENTS HISTORY ════════════════════════ */}
      {tab === 'history' && (
        <div>
          <StatCards stats={[
            { label:'Total Entries',   value: histEntries.length,   color:'#3498db' },
            { label:'Total Revenue',   value: fmtKES(histTotal),    color:'#c8a96e' },
            { label:'Cash',            value: fmtKES(histEntries.filter(e=>e.payment_mode==='cash').reduce((s,e)=>s+parseFloat(e.total||e.amount||0),0)), color:'#2ecc71' },
            { label:'M-Pesa',          value: fmtKES(histEntries.filter(e=>e.payment_mode==='mpesa').reduce((s,e)=>s+parseFloat(e.total||e.amount||0),0)), color:'#00b300' },
            { label:'Card / Other',    value: fmtKES(histEntries.filter(e=>!['cash','mpesa'].includes(e.payment_mode)).reduce((s,e)=>s+parseFloat(e.total||e.amount||0),0)), color:'#9b59b6' },
          ]} />

          <div style={{ display:'flex', gap:6, marginBottom:12 }}>
            {[{id:'today',label:"Today"},{id:'month',label:"This Month"},{id:'all',label:"All Records"}].map(f=>(
              <button key={f.id} onClick={()=>setHistFilter(f.id)} style={{
                padding:'5px 14px', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer',
                border:`1px solid ${histFilter===f.id?'var(--primary)':'var(--border)'}`,
                background:histFilter===f.id?'var(--primary)22':'var(--bgInput)',
                color:histFilter===f.id?'var(--primary)':'var(--textMuted)',
              }}>{f.label}</button>
            ))}
            <span style={{ marginLeft:'auto', fontSize:11, color:'var(--textMuted)', alignSelf:'center' }}>
              {histEntries.length} record{histEntries.length!==1?'s':''}
            </span>
          </div>

          <Card title="Pool & Kids Zone — Payment History">
            {histEntries.length === 0 ? <Empty text="No entries for this period" /> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'var(--bgInput)' }}>
                      {['Date & Time','Visitor','Type','Adults','Children','Swim','Extras','Total','Payment','Ref'].map(h=>(
                        <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, fontWeight:700,
                          color:'var(--textMuted)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap',
                          letterSpacing:1, textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {histEntries.slice().reverse().map((e,i)=>{
                      const total   = parseFloat(e.total||e.amount||0);
                      const extras  = parseFloat(e.extras_total||0);
                      const swim    = total - extras;
                      const mode    = e.payment_mode||'cash';
                      const color   = pmColor(mode);
                      const typeStr = SWIM_RATES.find(r=>r.type===e.visitor_type)?.label || (e.rate_type||e.visitor_type||'—').replace(/_/g,' ');
                      return (
                        <tr key={e.id||i} style={{ borderBottom:'1px solid var(--border)' }}>
                          <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11, whiteSpace:'nowrap' }}>
                            <div>{e.created_at?.slice(0,10)||'—'}</div>
                            <div style={{ fontSize:10 }}>{e.created_at?new Date(e.created_at).toLocaleTimeString('en-KE',{hour:'2-digit',minute:'2-digit'}):''}</div>
                          </td>
                          <td style={{ padding:'8px 10px', fontWeight:700, color:'var(--textPrimary)', whiteSpace:'nowrap' }}>{e.visitor_name}</td>
                          <td style={{ padding:'8px 10px', color:'var(--textSecond)', fontSize:11, textTransform:'capitalize' }}>{typeStr}</td>
                          <td style={{ padding:'8px 10px', color:'var(--textSecond)', textAlign:'center' }}>{e.adults||1}</td>
                          <td style={{ padding:'8px 10px', color:'var(--textSecond)', textAlign:'center' }}>{e.children||0}</td>
                          <td style={{ padding:'8px 10px', color:'#c8a96e', fontWeight:600, whiteSpace:'nowrap' }}>
                            {swim>0 ? fmtKES(swim) : <span style={{ color:'#2ecc71', fontWeight:700 }}>FOC</span>}
                          </td>
                          <td style={{ padding:'8px 10px', whiteSpace:'nowrap', fontWeight:extras>0?700:400, color:extras>0?'#9b59b6':'var(--textFaint)' }}>
                            {extras>0 ? fmtKES(extras) : '—'}
                          </td>
                          <td style={{ padding:'8px 10px', fontWeight:800, color:'#c8a96e', whiteSpace:'nowrap', fontSize:13 }}>
                            {fmtKES(total)}
                          </td>
                          <td style={{ padding:'8px 10px' }}>
                            <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px',
                              borderRadius:4, fontSize:10, fontWeight:700,
                              background:color+'22', color:color }}>
                              <span style={{ width:6,height:6,borderRadius:'50%',background:color,display:'inline-block' }} />
                              {mode.replace('_',' ')}
                            </span>
                          </td>
                          <td style={{ padding:'8px 10px', color:'#2ecc71', fontFamily:'monospace', fontSize:10 }}>
                            {e.transaction_code||'—'}
                          </td>
                          <td style={{ padding:'8px 6px' }}>
                            <button style={{ ...btnSm, fontSize:10, padding:'3px 8px' }} onClick={() => printPoolReceipt(e)}>🖨</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background:'var(--bgInput)' }}>
                      <td colSpan={7} style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11, fontWeight:600 }}>
                        Total ({histEntries.length} entries)
                      </td>
                      <td style={{ padding:'8px 10px', color:'#c8a96e', fontWeight:900, fontSize:14 }}>
                        {fmtKES(histTotal)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══════════════════════════ RATES & INFO ════════════════════════════ */}
      {tab === 'rates' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16 }}>
          <Card title="Swimming Pool Rates">
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  {['Visitor Category','Rate / Person'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', color:'var(--primary)', textAlign: h==='Rate / Person'?'right':'left',
                      fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:1,
                      borderBottom:'2px solid var(--primary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SWIM_RATES.map(r=>(
                  <tr key={r.type} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'11px 10px', color:'var(--textPrimary)', fontWeight:600 }}>{r.label}</td>
                    <td style={{ padding:'11px 10px', color:r.price===0?'#2ecc71':'#c8a96e', fontWeight:800, textAlign:'right', fontSize:14 }}>
                      {r.price===0 ? 'Complimentary' : fmtKES(r.price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop:14, padding:'12px 14px', background:'var(--bgInfo)', borderRadius:6, fontSize:12, color:'var(--textSecond)', lineHeight:1.9 }}>
              <div style={{ fontWeight:700, color:'var(--textPrimary)', marginBottom:6 }}>📋 Pool Rules & Info</div>
              <div>🕕 Hours: <strong>6:00 AM – 10:00 PM daily</strong></div>
              <div>👶 Children under 5 must be supervised at all times</div>
              <div>🩱 Proper swimwear required in pool</div>
              <div>🏨 Hotel guests: swimming is complimentary</div>
              <div>🩱 Swim gear available for hire at reception</div>
            </div>
          </Card>

          <Card title="Extras & Hire Rates">
            {EXTRA_CATS.filter(c=>c.id!=='all').map(cat=>(
              <div key={cat.id} style={{ marginBottom:16 }}>
                <div style={{ fontSize:10, fontWeight:800, color:'var(--primary)', textTransform:'uppercase',
                  letterSpacing:2, marginBottom:8, paddingBottom:5, borderBottom:'1px solid var(--border)' }}>
                  {cat.label}
                </div>
                {EXTRAS.filter(e=>e.cat===cat.id).map(ex=>(
                  <div key={ex.id} style={{ display:'flex', alignItems:'center', gap:10,
                    padding:'7px 4px', borderBottom:'1px solid var(--border)' }}>
                    <div style={{ width:30, height:30, borderRadius:6, background:'var(--bgInput)',
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Ico d={ex.icon} size={15} stroke="var(--textMuted)" />
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'var(--textPrimary)' }}>{ex.label}</div>
                      <div style={{ fontSize:10, color:'var(--textMuted)' }}>{ex.desc}</div>
                    </div>
                    <span style={{ fontWeight:800, color:'#c8a96e', fontSize:13, whiteSpace:'nowrap' }}>
                      {fmtKES(ex.price)}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </Card>
        </div>
      )}
      {showComp && (
        <ComplimentaryModal
          amount={totalAmt}
          guestName={form.visitor_name}
          notify={notify}
          onClose={() => setShowComp(false)}
          onSubmitted={() => { setForm(blank); setCart([]); load(); }}
        />
      )}
    </div>
  );
}

function BillLine({ label, amount, foc }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 0',
      borderBottom:'1px solid var(--border)', fontSize:12 }}>
      <span style={{ color:'var(--textSecond)' }}>{label}</span>
      <span style={{ fontWeight:700, color:foc?'#2ecc71':'#c8a96e' }}>
        {foc ? 'FOC' : fmtKES(amount)}
      </span>
    </div>
  );
}
